import { getSupabase } from '../auth/supabase.js'
import {
  commitGenerationDelivery as commitGenerationDeliveryStore,
  deleteStore, readAllStore, readStore, writeStore,
} from '../storage/db.js'
import { mergeView, normalizeDocument, scanAssetUris, stripViewport } from './document.js'
// The document schema registry was cut with the product layer. Documents carry
// no schemaVersion in this build, so nothing can be "from the future"; the
// guards below stay as inert hooks for a future registry.
function isFutureDocumentSchema() { return false }
import { localViewState, restoreLocalView, sameCamera } from './view-state.js'

function browserDeviceId() {
  const key = 'blaze.web.device-id'
  let value = localStorage.getItem(key)
  if (!value) { value = crypto.randomUUID(); localStorage.setItem(key, value) }
  return value
}

function createCanvasId() { return `cv_${crypto.randomUUID().replaceAll('-', '')}` }

function environmentReadOnlyError() {
  const error = new Error('environment_read_only')
  error.code = 'environment_read_only'
  return error
}

function repositoryLifecycleError(code) {
  const error = new Error(code)
  error.code = code
  return error
}

function canvasDeletedError(id) {
  const error = new Error('canvas_deleted')
  error.code = 'canvas_deleted'
  error.canvasId = id
  return error
}

function summaryTimestamp(value) {
  const numeric = Number(value)
  if (Number.isFinite(numeric) && numeric > 0) return numeric
  const parsed = value ? new Date(value).getTime() : NaN
  return Number.isFinite(parsed) ? parsed : 0
}

// Order-insensitive comparison against Postgres jsonb payloads: jsonb does not
// preserve object key order, so JSON.stringify equality would false-negative.
// Arrays stay order-sensitive (item order is document content).
function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`
  }
  return JSON.stringify(value)
}

function comparableDocument(id, value) {
  try { return normalizeDocument(id, value) } catch { return value }
}

export class CanvasRepository extends EventTarget {
  constructor(assetMap, options = {}) {
    super()
    this.assetMap = assetMap
    this.readOnly = options.readOnly === true
    this.channels = new Map()
    this.supabase = options.supabase || getSupabase
    this.store = options.store || { delete: deleteStore, read: readStore, readAll: readAllStore, write: writeStore }
    this.commitDeliveryStore = options.commitGenerationDelivery || commitGenerationDeliveryStore
    this.deviceId = options.deviceId || browserDeviceId()
    // writerId doubles as sync_push device_id and the realtime echo-suppression
    // key. It must be the PERSISTENT browser identity: a per-page-load UUID made
    // every earlier session of this same browser look like "another device",
    // which was the main source of false conflict dialogs. Same-device version
    // mismatches now self-heal server-side (sync_push same-device rebase).
    this.writerId = options.writerId || this.deviceId
    this.viewSyncDelay = options.viewSyncDelay ?? 2_000
    this.retryBaseDelay = options.retryBaseDelay ?? 1_000
    this.retryMaxDelay = options.retryMaxDelay ?? 60_000
    this.random = options.random || Math.random
    this.onlineTarget = options.onlineTarget ?? (typeof window !== 'undefined' ? window : null)
    this.viewTimers = new Map()
    this.retryTimers = new Map()
    this.quiesced = false
    this.lifecycleController = new AbortController()
    this.onOnline = () => {
      if (this.quiesced) return
      for (const timer of this.retryTimers.values()) clearTimeout(timer)
      this.retryTimers.clear()
      void this.#flushReadyOutboxes({ force: true }).catch(() => {})
      void this.#flushPendingViews().catch(() => {})
    }
    this.onAssetUploadStatus = event => {
      if (this.quiesced) return
      if (event.detail?.status === 'committed') void this.#flushReadyOutboxes().catch(() => {})
      if (event.detail?.status === 'failed' || event.detail?.status === 'cancelled') void this.#markUploadFailure(event.detail.hash).catch(() => {})
    }
    if (!this.readOnly) {
      this.onlineTarget?.addEventListener?.('online', this.onOnline)
      this.assetMap.addEventListener?.('upload-status', this.onAssetUploadStatus)
    }
  }

  // options.previews: false returns summaries immediately (previewAssets: [])
  // so the list page can paint cards as soon as the index answers, then
  // hydrate thumbnails via attachPreviews() — instead of holding the whole
  // grid hostage to the slowest preview resolve.
  async list(options = {}) {
    this.#assertActive()
    const withPreviews = options.previews !== false
    const sb = this.supabase()
    if (sb) {
      let request = sb.from('canvas_index').select('canvas_id,name,item_count,preview_hashes,created_at,updated_at').eq('deleted', false).order('updated_at', { ascending: false })
      request = this.#abortable(request)
      const { data, error } = await request
      this.#assertActive()
      if (!error) {
        const summaries = await this.#overlayLocalEdits((data || []).map(row => ({
          id: row.canvas_id,
          name: row.name,
          itemCount: row.item_count,
          previews: (row.preview_hashes || []).map(hash => ({ hash })),
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        })))
        return withPreviews ? this.#attachPreviewAssets(summaries) : summaries.map(summary => ({ ...summary, previewAssets: [] }))
      }
    }
    const [local, outboxes] = await Promise.all([
      this.store.readAll('canvas_working_copies'),
      this.store.readAll('sync_outbox'),
    ])
    const hiddenDeletes = new Set(outboxes.filter(row => row.deleted).map(row => row.canvasId))
    const summaries = local.filter(row => !row.deleted && !hiddenDeletes.has(row.id)).map(row => ({
      id: row.id,
      name: row.document?.name || 'Untitled',
      itemCount: row.document?.items?.length || 0,
      previews: scanAssetUris(row.document).slice(0, 5),
      createdAt: row.document?.createdAt,
      updatedAt: row.updatedAt,
    })).sort((a, b) => Number(b.updatedAt) - Number(a.updatedAt))
    return withPreviews ? this.#attachPreviewAssets(summaries) : summaries.map(summary => ({ ...summary, previewAssets: [] }))
  }

  attachPreviews(summaries) {
    this.#assertActive()
    return this.#attachPreviewAssets(summaries || [])
  }

  // canvas_index is a projection refreshed only by an accepted sync_push, so a
  // canvas whose assets are still uploading reports a stale name/count/cover —
  // and a brand-new canvas may be missing from it entirely. Any canvas with
  // un-pushed local edits (a sync_outbox row) reads its summary from the local
  // working copy instead; local-only canvases with pending pushes are appended.
  async #overlayLocalEdits(summaries) {
    let locals, outboxes
    try {
      [locals, outboxes] = await Promise.all([
        this.store.readAll('canvas_working_copies'),
        this.store.readAll('sync_outbox'),
      ])
      this.#assertActive()
    } catch (error) {
      if (this.quiesced) throw error
      return summaries
    }
    const hiddenDeletes = new Set(outboxes.filter(row => row.deleted).map(row => row.canvasId))
    const pending = new Set(outboxes.filter(row => !row.deleted).map(row => row.canvasId))
    if (!pending.size && !hiddenDeletes.size) return summaries
    const byId = new Map(summaries.map(summary => [summary.id, summary]))
    for (const id of hiddenDeletes) byId.delete(id)
    for (const row of locals) {
      if (row.deleted || !pending.has(row.id) || !row.document) continue
      const overlay = {
        name: row.document.name || 'Untitled',
        itemCount: row.document.items?.length || 0,
        previews: scanAssetUris(row.document).slice(0, 5),
      }
      const cloud = byId.get(row.id)
      byId.set(row.id, cloud
        ? { ...cloud, ...overlay }
        : { id: row.id, ...overlay, createdAt: row.document.createdAt, updatedAt: row.updatedAt })
    }
    return [...byId.values()].sort((a, b) => summaryTimestamp(b.updatedAt) - summaryTimestamp(a.updatedAt))
  }

  async #attachPreviewAssets(summaries) {
    const refs = summaries.flatMap(summary => summary.previews || [])
    if (!refs.length || typeof this.assetMap.resolveHashes !== 'function') {
      return summaries.map(summary => ({ ...summary, previewAssets: [] }))
    }
    try {
      const resolved = await this.assetMap.resolveHashes(refs)
      this.#assertActive()
      const byHash = new Map(resolved.map(asset => [asset.hash, asset]))
      return summaries.map(summary => ({
        ...summary,
        previewAssets: (summary.previews || []).map(ref => byHash.get(ref.hash)).filter(Boolean),
      }))
    } catch (error) {
      if (this.quiesced) throw error
      return summaries.map(summary => ({ ...summary, previewAssets: [] }))
    }
  }

  async remote(id) {
    this.#assertActive()
    const sb = this.supabase()
    if (!sb?.from) return null
    let request = sb.from('synced_records').select('entity_id,version,deleted,payload,updated_at').eq('kind', 'canvas').eq('entity_id', id).maybeSingle()
    request = this.#abortable(request)
    const { data, error } = await request
    this.#assertActive()
    if (error) throw error
    return data
  }

  async remoteView(id) {
    this.#assertActive()
    const sb = this.supabase()
    if (!sb?.from) return null
    let request = sb.from('canvas_user_state')
      .select('canvas_id,center_x,center_y,zoom,device_id,updated_at')
      .eq('canvas_id', id)
      .maybeSingle()
    request = this.#abortable(request)
    const { data, error } = await request
    this.#assertActive()
    if (error) throw error
    if (!data) return null
    return {
      canvasId: data.canvas_id,
      centerX: Number(data.center_x),
      centerY: Number(data.center_y),
      zoom: Number(data.zoom),
      deviceId: data.device_id || null,
      cloudUpdatedAt: data.updated_at || null,
      syncStatus: 'synced',
    }
  }

  async load(id, viewportSize = null) {
    this.#assertActive()
    let [local, localView, outbox] = await Promise.all([
      this.store.read('canvas_working_copies', id), this.store.read('canvas_view_state', id), this.store.read('sync_outbox', id),
    ])
    this.#assertActive()
    const [remoteResult, viewResult] = await Promise.allSettled([this.remote(id), this.remoteView(id)])
    this.#assertActive()
    // A durable local delete intent is sufficient to hide the canvas even if
    // its recovery bytes are absent and the remote read is offline. Throwing
    // here would make a direct URL look indeterminate and risks callers
    // treating the id as reusable.
    if (remoteResult.status === 'rejected' && !local && !outbox?.deleted) throw remoteResult.reason
    const remote = remoteResult.status === 'fulfilled' ? remoteResult.value : null
    const cloudView = viewResult.status === 'fulfilled' ? viewResult.value : null
    if (remote?.deleted) {
      const marker = await this.#recordTombstone(id, remote)
      return { status: 'deleted', version: marker.version, remote: true }
    }
    if (local?.deleted) {
      return { status: 'deleted', version: Number(local.version) || 0, remote: local.remote === true }
    }
    if (outbox?.deleted) {
      return {
        status: 'deleted', version: Math.max(Number(outbox.baseVersion) || 0, Number(local?.version) || 0),
        pending: outbox.status !== 'conflict', conflict: outbox.status === 'conflict',
      }
    }
    if (!remote && !local) return { status: 'missing' }
    let adoptedLocalRevision = 0
    if (outbox && !outbox.deleted && remote && stableStringify(comparableDocument(id, outbox.document)) === stableStringify(comparableDocument(id, remote.payload))) {
      // The server already holds exactly this content (our own write whose ack
      // was lost, or a rebadged echo) — nothing local to protect. Adopt.
      adoptedLocalRevision = Number(outbox.localRevision) || 0
      await this.store.delete('sync_outbox', id)
      outbox = null
    } else if (outbox?.status === 'conflict' && !outbox.deleted) {
      // A parked conflict re-adjudicates under server-side device attribution:
      // flush again and let sync_push decide (same device → self-heal accept;
      // genuinely foreign → it re-parks and the dialog returns). Conflicts
      // parked by the pre-attribution client stop haunting every open.
      outbox = { ...outbox, status: 'pending', updatedAt: Date.now() }
      await this.store.write('sync_outbox', outbox)
    }
    const useLocal = outbox?.status === 'pending' && !outbox.deleted
    const candidate = useLocal && local ? local.document : remote?.payload || local?.document
    if (!candidate) return { status: 'missing' }
    const readOnly = this.readOnly || isFutureDocumentSchema(candidate)
    const document = normalizeDocument(id, candidate)
    const version = Number(useLocal ? outbox?.baseVersion : remote?.version) || 0
    const selectedView = localView?.syncStatus === 'pending' ? localView : cloudView || localView
    const restoredView = restoreLocalView(selectedView, viewportSize)
    const localRevision = Math.max(
      Number(local?.localRevision) || 0,
      Number(outbox?.localRevision) || 0,
    )
    const lastSyncedLocalRevision = Math.max(
      Number(local?.lastSyncedLocalRevision) || 0,
      adoptedLocalRevision,
    )
    const writes = [this.store.write('canvas_working_copies', {
      id, document, version, localRevision, lastSyncedLocalRevision, updatedAt: Date.now(),
    })]
    if (selectedView) {
      writes.push(this.store.write('canvas_view_state', {
        ...selectedView, ...restoredView, canvasId: id,
        updatedAt: localView?.syncStatus === 'pending' ? localView.updatedAt : Date.now(),
      }))
    }
    await Promise.all(writes)
    this.#assertActive()
    if (!this.readOnly && localView?.syncStatus === 'pending') this.#scheduleViewPush(id)
    if (!this.readOnly && useLocal) {
      // Un-pushed local edits ride out to the server as soon as their assets
      // are committed — without waiting for the next edit or upload event.
      void this.assetMap.allCommitted(document)
        .then(ready => (ready ? this.flush(id) : null))
        .catch(() => {})
    }
    return { status: 'loaded', document: mergeView(document, restoredView), version, localRevision, conflict: false, readOnly }
  }

  // Staging shares the per-canvas chain with flushes: stageLocal's
  // read-modify-write of canvas_working_copies spans several awaits, and a
  // flush completing inside that window used to get its freshly-accepted
  // version clobbered back down — the next push then CAS-failed against our
  // own write and false-alarmed the conflict dialog.
  stageLocal(id, rawDocument, baseVersion, viewportSize = null, options = {}) {
    if (this.readOnly) return Promise.reject(environmentReadOnlyError())
    return this.#chain(id, () => this.#stageLocalNow(id, rawDocument, baseVersion, viewportSize, options))
  }

  // The page lifecycle owner uses this one narrow escape hatch after dispose()
  // has stopped every remote source. It can only persist the already-captured
  // document/camera to account-local IndexedDB; ordinary repository entry
  // points remain closed and #stageLocalNow cannot schedule while quiesced.
  finalizeLocalSnapshot(id, rawDocument, baseVersion, viewportSize = null, options = {}) {
    if (!this.quiesced) return Promise.reject(repositoryLifecycleError('canvas_repository_not_quiesced'))
    if (this.readOnly) return Promise.reject(environmentReadOnlyError())
    return this.#chain(
      id,
      () => this.#stageLocalNow(id, rawDocument, baseVersion, viewportSize, options),
      { allowQuiesced: true },
    )
  }

  async #stageLocalNow(id, rawDocument, baseVersion, viewportSize = null, options = {}) {
    const [current, pending, previousView] = await Promise.all([
      this.store.read('canvas_working_copies', id),
      this.store.read('sync_outbox', id),
      this.store.read('canvas_view_state', id),
    ])
    this.#throwIfDeleted(id, current, pending)
    if (isFutureDocumentSchema(rawDocument) || isFutureDocumentSchema(current?.document)) {
      const error = new Error('canvas_schema_newer_read_only')
      error.code = 'canvas_schema_newer_read_only'
      throw error
    }
    const currentLocalRevision = Number(current?.localRevision) || 0
    const expectedLocalRevision = options?.expectedLocalRevision == null
      ? currentLocalRevision
      : Math.max(0, Number(options.expectedLocalRevision) || 0)
    // A snapshot captured by canvas-entry's 300 ms debounce predates a paid
    // generation delivery when its expected revision is lower. Never let that
    // old snapshot erase the atomically committed result.
    if (expectedLocalRevision < currentLocalRevision) {
      return {
        ok: true,
        stale: true,
        document: current.document,
        version: Number(current.version) || Number(baseVersion) || 0,
        localRevision: currentLocalRevision,
      }
    }
    const document = normalizeDocument(id, stripViewport(rawDocument), current?.document)
    const nextView = localViewState(id, rawDocument, viewportSize, previousView)
    const viewChanged = !previousView || !sameCamera(previousView, nextView)
    const view = {
      ...nextView,
      syncStatus: viewChanged ? 'pending' : previousView.syncStatus,
      cloudUpdatedAt: previousView?.cloudUpdatedAt || null,
    }
    if (!pending && current?.version > 0 && JSON.stringify(current.document) === JSON.stringify(document)) {
      await this.store.write('canvas_view_state', view)
      if (view.syncStatus === 'pending') this.#scheduleViewPush(id)
      this.#status(id, 'local')
      return { ok: true, unchanged: true, document, version: current.version, localRevision: currentLocalRevision }
    }
    const nextLocalRevision = currentLocalRevision + 1
    const conflict = pending?.status === 'conflict'
    const outbox = conflict
      ? { ...pending, document, localRevision: nextLocalRevision, updatedAt: Date.now() }
      : {
          canvasId: id, document, baseVersion, localRevision: nextLocalRevision,
          status: 'pending', attempts: pending?.attempts || 0, updatedAt: Date.now(),
        }
    await Promise.all([
      // Never downgrade the tracked version: an out-of-band flush (asset
      // upload completion) may have already advanced it past the caller's
      // baseline. The tracked version is the flush-time source of truth.
      this.store.write('canvas_working_copies', {
        id, document,
        version: Math.max(Number(baseVersion) || 0, Number(current?.version) || 0),
        localRevision: nextLocalRevision,
        lastSyncedLocalRevision: Number(current?.lastSyncedLocalRevision) || 0,
        updatedAt: Date.now(),
      }),
      this.store.write('canvas_view_state', view),
      this.store.write('sync_outbox', outbox),
    ])
    if (view.syncStatus === 'pending') this.#scheduleViewPush(id)
    this.#status(id, conflict ? 'conflict' : 'local', { dirty: true })
    return { ok: !conflict, conflict, document, version: baseVersion, localRevision: nextLocalRevision }
  }

  async save(id, rawDocument, baseVersion, viewportSize = null) {
    if (this.readOnly) throw environmentReadOnlyError()
    this.#assertActive()
    const staged = await this.stageLocal(id, rawDocument, baseVersion, viewportSize)
    this.#assertActive()
    if (staged.conflict || staged.unchanged) return staged
    const assetState = this.assetMap.commitState
      ? await this.assetMap.commitState(staged.document)
      : (await this.assetMap.allCommitted(staged.document) ? 'committed' : 'uploading')
    this.#assertActive()
    if (assetState !== 'committed') {
      const status = assetState === 'failed' || assetState === 'cancelled' ? 'upload-error' : 'uploading'
      this.#status(id, status, { dirty: true })
      return { ok: true, localOnly: true, uploading: status === 'uploading', uploadError: status === 'upload-error', version: baseVersion }
    }
    return this.flush(id)
  }

  // All writers of a canvas's outbox/working copy — stageLocal, save's flush,
  // and the asset-upload-completion sweep (#flushReadyOutboxes) — run through
  // one per-canvas chain: a concurrent second call waits, then operates on the
  // fresh state instead of re-pushing or clobbering with a stale baseline
  // (which the server's CAS would misreport as a foreign conflict).
  #chains = new Map()
  #chain(id, task, options = {}) {
    const prev = this.#chains.get(id) || Promise.resolve()
    const next = prev.catch(() => {}).then(() => {
      if (this.quiesced && options.allowQuiesced !== true) throw repositoryLifecycleError('canvas_repository_quiesced')
      return task()
    })
    this.#chains.set(id, next)
    const cleanup = () => { if (this.#chains.get(id) === next) this.#chains.delete(id) }
    // finally() creates a second rejecting promise when the task fails. Using
    // both then branches keeps cleanup observable without an unhandled shadow
    // rejection (important for the intentional future-schema write gate).
    void next.then(cleanup, cleanup)
    return next
  }

  flush(id) {
    if (this.readOnly) return Promise.reject(environmentReadOnlyError())
    return this.#chain(id, () => this.#flushNow(id))
  }

  commitGenerationDelivery(id, input) {
    if (this.readOnly) return Promise.reject(environmentReadOnlyError())
    return this.#chain(id, async () => {
      const [working, outbox] = await Promise.all([
        this.store.read('canvas_working_copies', id),
        this.store.read('sync_outbox', id),
      ])
      this.#throwIfDeleted(id, working, outbox)
      const result = await this.commitDeliveryStore({ ...input, canvasId: id })
      this.#status(id, 'local', {
        dirty: true,
        deliveryId: result.deliveryId,
        localRevision: result.localRevision,
      })
      return result
    })
  }

  async isGenerationDeliverySynced(id, delivery) {
    const working = await this.store.read('canvas_working_copies', id)
    this.#assertActive()
    if (working?.deleted) return false
    const localRevision = delivery?.localRevision
    if (!Number.isSafeInteger(localRevision) || localRevision <= 0) return false
    if (Number(working?.lastSyncedLocalRevision) < localRevision) return false
    // Revision alone is not proof the paid items survived: a stale-but-newer
    // renderer stage (e.g. after a failed placeholder swap) can legitimately
    // advance the revision with a document that never contained them. Only a
    // synced document that still carries every delivered item id settles the
    // delivery (review W2).
    const itemIds = Array.isArray(delivery?.itemIds) ? delivery.itemIds.filter(Boolean).map(String) : []
    if (itemIds.length) {
      const present = new Set((working?.document?.items || []).map(item => String(item?.id || '')))
      if (itemIds.some(itemId => !present.has(itemId))) return false
    }
    return true
  }

  async #flushNow(id, rebased = false) {
    this.#assertActive()
    const [outbox, tracked] = await Promise.all([
      this.store.read('sync_outbox', id),
      this.store.read('canvas_working_copies', id),
    ])
    this.#assertActive()
    if (tracked?.deleted) {
      if (outbox) await this.store.delete('sync_outbox', id)
      throw canvasDeletedError(id)
    }
    if (!outbox) {
      const version = tracked?.version
      return Number.isSafeInteger(version) && version >= 0
        ? { ok: false, unchanged: true, version }
        : { ok: false, unchanged: true }
    }
    if (outbox.status === 'conflict') return { ok: false, conflict: true }
    // Push against the newest version this device has had accepted, not the
    // (possibly stale) baseline captured at staging time — an out-of-band
    // flush may have advanced the server since. Tracked versions only ever
    // reflect our own accepted pushes and loads, so a genuinely foreign write
    // still trips the server-side CAS.
    const base = Math.max(Number(outbox.baseVersion) || 0, Number(tracked?.version) || 0)
    const sb = this.supabase()
    if (!sb) {
      this.#status(id, outbox.deleted ? 'pending-delete' : 'local', { dirty: true })
      return { ok: true, localOnly: true, pendingDelete: outbox.deleted === true, version: base }
    }
    this.#status(id, 'syncing')
    const deleting = outbox.deleted === true
    const record = { kind: 'canvas', entity_id: id, parent_id: null, deleted: false, device_id: this.writerId, payload: outbox.document, expected_prev_version: base }
    const retry = async error => {
      this.#assertActive()
      const [latestWorking, latestOutbox] = await Promise.all([
        this.store.read('canvas_working_copies', id),
        this.store.read('sync_outbox', id),
      ])
      this.#assertActive()
      if (latestWorking?.deleted || (!deleting && latestOutbox?.deleted)) {
        if (latestWorking?.deleted && latestOutbox) await this.store.delete('sync_outbox', id)
        throw canvasDeletedError(id)
      }
      const attempts = Number(outbox.attempts || 0) + 1
      const delay = this.#retryDelay(attempts)
      await this.store.write('sync_outbox', {
        ...outbox,
        attempts,
        lastError: String(error?.message || error),
        nextRetryAt: Date.now() + delay,
        updatedAt: Date.now(),
      })
      this.#assertActive()
      this.#scheduleRetry(id, delay)
      this.#status(id, deleting ? 'pending-delete' : 'local', { dirty: true })
      return { ok: true, localOnly: true, retryable: true, pendingDelete: deleting, version: base }
    }
    let data
    try {
      let request = deleting
        ? sb.rpc('sync_delete_strict', {
            p_kind: 'canvas',
            p_entity_id: id,
            p_expected_prev_version: base,
            p_device_id: this.writerId,
          })
        : sb.rpc('sync_push', { p_records: [record] })
      request = this.#abortable(request)
      const response = await request
      this.#assertActive()
      if (response.error) throw response.error
      if (deleting) {
        const row = Array.isArray(response.data) ? response.data[0] : response.data
        if (row?.ok === true) {
          data = { accepted: [{ kind: 'canvas', entity_id: id, version: Number(row.version || row.current_version) || base }], conflicts: [] }
        } else if (row?.error === 'conflict' || Number(row?.current_version) > base) {
          data = { accepted: [], conflicts: [{ kind: 'canvas', entity_id: id, current_version: Number(row?.current_version) || 0 }] }
        } else {
          throw new Error(`canvas_delete_${row?.error || 'not_accepted'}`)
        }
      } else {
        data = response.data
      }
    } catch (error) {
      if (this.quiesced) throw repositoryLifecycleError('canvas_repository_quiesced')
      return retry(error)
    }
    const conflicts = Array.isArray(data?.conflicts) ? data.conflicts : []
    const acceptedRows = Array.isArray(data?.accepted) ? data.accepted : []
    const conflict = conflicts.find(item => item?.kind === 'canvas' && item?.entity_id === id)
    const accepted = acceptedRows.find(item => item?.kind === 'canvas' && item?.entity_id === id)
    if (!deleting && (!conflict && !accepted || conflict && accepted)) {
      return retry(new Error('canvas_sync_not_acknowledged'))
    }
    if (conflict) {
      // The RPC reports the stored row's device_id. If it is OUR device the
      // mismatch was a race between two of our own pushes (the server-side
      // same-device self-heal lost its CAS) — adopt the server version and
      // retry once instead of alarming the user. Anything else is a genuine
      // foreign write: park and let the dialog decide.
      const currentVersion = Number(conflict.current_version)
      if (!Number.isSafeInteger(currentVersion) || currentVersion <= 0) {
        return retry(new Error('canvas_sync_malformed_conflict'))
      }
      let remoteRow = null
      if (!deleting) {
        try {
          remoteRow = await this.remote(id)
        } catch (error) {
          // A same-device rebase is safe only after a fresh read proves the
          // current server row is not a tombstone. Keep the outbox parked on
          // read failure instead of turning an old edit into a resurrection.
          return retry(error)
        }
        if (remoteRow?.deleted) {
          const marker = await this.#recordTombstone(id, remoteRow)
          this.#status(id, 'deleted', { version: marker.version, dirty: false })
          throw canvasDeletedError(id)
        }
      }
      if (!deleting && conflict.device_id === this.writerId && !rebased) {
        await Promise.all([
          this.store.write('sync_outbox', { ...outbox, baseVersion: currentVersion, updatedAt: Date.now() }),
          this.store.write('canvas_working_copies', {
            id, document: tracked?.document || outbox.document, version: currentVersion,
            localRevision: Math.max(Number(tracked?.localRevision) || 0, Number(outbox.localRevision) || 0),
            lastSyncedLocalRevision: Number(tracked?.lastSyncedLocalRevision) || 0,
            updatedAt: Date.now(),
          }),
        ])
        this.#assertActive()
        return this.#flushNow(id, true)
      }
      // Foreign attribution but identical content: the server already holds
      // exactly what we were about to push — adopt silently, never alarm.
      if (!deleting && remoteRow && !remoteRow.deleted && stableStringify(comparableDocument(id, remoteRow.payload)) === stableStringify(comparableDocument(id, outbox.document))) {
        const version = Number(remoteRow.version) || currentVersion
        const localRevision = Math.max(Number(tracked?.localRevision) || 0, Number(outbox.localRevision) || 0)
        const lastSyncedLocalRevision = Math.max(
          Number(tracked?.lastSyncedLocalRevision) || 0,
          Number(outbox.localRevision) || 0,
        )
        await Promise.all([
          this.store.delete('sync_outbox', id),
          this.store.write('canvas_working_copies', {
            id, document: outbox.document, version,
            localRevision,
            lastSyncedLocalRevision,
            updatedAt: Date.now(),
          }),
        ])
        this.#assertActive()
        this.#status(id, 'cloud', { version, dirty: false })
        return { ok: true, version }
      }
      await this.store.write('sync_outbox', { ...outbox, status: 'conflict', currentVersion, updatedAt: Date.now() })
      this.#assertActive()
      this.#clearRetry(id)
      this.#status(id, 'conflict', { operation: deleting ? 'delete' : 'save' })
      return { ok: false, conflict: true, operation: deleting ? 'delete' : 'save', currentVersion }
    }
    if (deleting && !accepted) return retry(new Error('canvas_delete_not_accepted'))
    const version = Number(accepted?.version)
    if (!Number.isSafeInteger(version) || version <= 0) {
      return retry(new Error('canvas_sync_malformed_acceptance'))
    }
    if (deleting) {
      await this.#finalizeLocalDelete(id, version)
      this.#status(id, 'deleted', { version, dirty: false })
      return { ok: true, deleted: true, version }
    }
    const localRevision = Math.max(Number(tracked?.localRevision) || 0, Number(outbox.localRevision) || 0)
    const lastSyncedLocalRevision = Math.max(
      Number(tracked?.lastSyncedLocalRevision) || 0,
      Number(outbox.localRevision) || 0,
    )
    await Promise.all([
      this.store.delete('sync_outbox', id),
      this.store.write('canvas_working_copies', {
        id, document: outbox.document, version,
        localRevision,
        lastSyncedLocalRevision,
        updatedAt: Date.now(),
      }),
    ])
    this.#assertActive()
    this.#clearRetry(id)
    this.#status(id, 'cloud', { version, dirty: false })
    return { ok: true, version }
  }

  async flushView(id) {
    if (this.readOnly) throw environmentReadOnlyError()
    this.#assertActive()
    clearTimeout(this.viewTimers.get(id))
    this.viewTimers.delete(id)
    const [candidate, working, outbox] = await Promise.all([
      this.store.read('canvas_view_state', id),
      this.store.read('canvas_working_copies', id),
      this.store.read('sync_outbox', id),
    ])
    this.#assertActive()
    this.#throwIfDeleted(id, working, outbox)
    if (!candidate || candidate.syncStatus !== 'pending') return { ok: true, unchanged: true }
    const sb = this.supabase()
    if (!sb?.rpc) return { ok: true, localOnly: true }
    let request = sb.rpc('save_canvas_user_state', {
      p_canvas_id: id,
      p_center_x: candidate.centerX,
      p_center_y: candidate.centerY,
      p_zoom: candidate.zoom,
      p_device_id: this.deviceId,
    })
    request = this.#abortable(request)
    const response = await request
    this.#assertActive()
    if (response.error) {
      const latest = await this.store.read('canvas_view_state', id)
      this.#assertActive()
      if (latest?.updatedAt === candidate.updatedAt) {
        await this.store.write('canvas_view_state', {
          ...latest, syncStatus: 'pending', lastError: String(response.error.message || response.error),
        })
        this.#assertActive()
      } else if (latest?.syncStatus === 'pending') this.#scheduleViewPush(id)
      return { ok: false, retryable: true }
    }
    const latest = await this.store.read('canvas_view_state', id)
    this.#assertActive()
    if (latest?.updatedAt !== candidate.updatedAt) {
      this.#scheduleViewPush(id)
      return { ok: true, superseded: true }
    }
    const row = Array.isArray(response.data) ? response.data[0] : response.data
    await this.store.write('canvas_view_state', {
      ...latest,
      syncStatus: 'synced',
      cloudUpdatedAt: row?.updated_at || new Date().toISOString(),
      lastError: undefined,
    })
    this.#assertActive()
    return { ok: true }
  }

  async create(name = 'Untitled') {
    if (this.readOnly) throw environmentReadOnlyError()
    this.#assertActive()
    const id = createCanvasId()
    const document = normalizeDocument(id, { id, name, items: [] })
    await this.store.write('canvas_working_copies', { id, document, version: 0, updatedAt: Date.now() })
    this.#assertActive()
    await this.save(id, document, 0)
    return id
  }

  async rename(id, name) {
    if (this.readOnly) throw environmentReadOnlyError()
    this.#assertActive()
    const loaded = await this.load(id)
    this.#assertActive()
    if (loaded?.status !== 'loaded' || !loaded.document) {
      if (loaded?.status === 'deleted') throw canvasDeletedError(id)
      throw new Error('canvas_not_found')
    }
    return this.save(id, { ...loaded.document, name }, loaded.version)
  }

  async duplicate(id, source = null) {
    if (this.readOnly) throw environmentReadOnlyError()
    this.#assertActive()
    const loaded = source ? { document: source } : await this.load(id)
    this.#assertActive()
    if (!loaded?.document) {
      if (loaded?.status === 'deleted') throw canvasDeletedError(id)
      throw new Error('canvas_not_found')
    }
    const nextId = createCanvasId()
    const document = normalizeDocument(nextId, { ...loaded.document, id: nextId, name: `${loaded.document.name} Copy`, createdAt: Date.now() })
    await this.save(nextId, document, 0)
    return nextId
  }

  delete(id) {
    if (this.readOnly) return Promise.reject(environmentReadOnlyError())
    return this.#chain(id, () => this.#deleteNow(id))
  }

  async #deleteNow(id) {
    this.#assertActive()
    const [tracked, previous, remoteResult] = await Promise.all([
      this.store.read('canvas_working_copies', id),
      this.store.read('sync_outbox', id),
      this.remote(id).then(value => ({ value }), error => ({ error })),
    ])
    this.#assertActive()
    if (tracked?.deleted) {
      if (previous) await this.store.delete('sync_outbox', id)
      return { ok: true, deleted: true, localOnly: tracked.remote !== true, version: Number(tracked.version) || 0 }
    }
    if (remoteResult.value?.deleted) {
      const marker = await this.#recordTombstone(id, remoteResult.value)
      this.#status(id, 'deleted', { version: marker.version, dirty: false })
      return { ok: true, deleted: true, version: marker.version }
    }
    if (previous?.deleted) return this.#flushNow(id)
    // A version-0 working copy with no outbox and a successful "not found"
    // remote read has never reached cloud sync. Strict delete requires a real
    // server version, so writing a tombstone here would leave it pending
    // forever. A failed remote read is deliberately excluded: it cannot prove
    // that the remote row is absent.
    if (!previous && !remoteResult.error && !remoteResult.value && (!tracked || Number(tracked.version) === 0)) {
      await this.#finalizeLocalDelete(id, 0, { remote: false })
      this.#status(id, 'deleted', { version: 0, dirty: false })
      return { ok: true, deleted: true, localOnly: true, version: 0 }
    }
    const baseVersion = Math.max(
      Number(remoteResult.value?.version) || 0,
      Number(tracked?.version) || 0,
      Number(previous?.baseVersion) || 0,
      Number(previous?.currentVersion) || 0,
    )
    const tombstone = {
      canvasId: id,
      deleted: true,
      document: {},
      baseVersion,
      status: 'pending',
      attempts: Number(previous?.attempts) || 0,
      updatedAt: Date.now(),
      ...(remoteResult.error ? { lastError: String(remoteResult.error?.message || remoteResult.error) } : {}),
    }
    // Durable intent first. Neither an offline read nor an RPC failure may
    // clear the only local recovery copy.
    await this.store.write('sync_outbox', tombstone)
    this.#assertActive()
    this.#status(id, 'pending-delete', { dirty: true })
    return this.#flushNow(id)
  }

  async #finalizeLocalDelete(id, version, options = {}) {
    this.#assertActive()
    clearTimeout(this.viewTimers.get(id))
    this.viewTimers.delete(id)
    this.#clearRetry(id)
    const sb = this.supabase()
    if (sb?.from) {
      try {
        let request = sb.from('canvas_user_state').delete().eq('canvas_id', id)
        request = this.#abortable(request)
        await request
        this.#assertActive()
      } catch (error) {
        if (this.quiesced) throw repositoryLifecycleError('canvas_repository_quiesced')
      }
    }
    await this.#recordTombstone(id, {
      version,
      deleted: true,
      remote: options.remote !== false,
      updated_at: new Date().toISOString(),
    })
  }

  async discardLocal(id) {
    if (this.readOnly) throw environmentReadOnlyError()
    this.#assertActive()
    const [working, outbox] = await Promise.all([
      this.store.read('canvas_working_copies', id),
      this.store.read('sync_outbox', id),
    ])
    this.#assertActive()
    this.#throwIfDeleted(id, working, outbox)
    await Promise.all([this.store.delete('sync_outbox', id), this.store.delete('canvas_working_copies', id)])
    this.#assertActive()
    return this.load(id)
  }

  // "保留本机修改" resolution for a parked conflict: rebase the outbox onto the
  // server's current version and push local content over it (explicit
  // user-chosen last-write-wins). Runs on the canvas chain; calls #flushNow
  // directly — flush() would enqueue behind this entry and deadlock.
  resolveConflictKeepLocal(id) {
    if (this.readOnly) return Promise.reject(environmentReadOnlyError())
    return this.#chain(id, async () => {
      const [outbox, tracked] = await Promise.all([
        this.store.read('sync_outbox', id),
        this.store.read('canvas_working_copies', id),
      ])
      this.#throwIfDeleted(id, tracked, outbox)
      if (!outbox) return { ok: true, unchanged: true }
      const remote = await this.remote(id)
      if (remote?.deleted) {
        const marker = await this.#recordTombstone(id, remote)
        this.#status(id, 'deleted', { version: marker.version, dirty: false })
        throw canvasDeletedError(id)
      }
      const base = Math.max(Number(outbox.currentVersion) || 0, Number(remote?.version) || 0)
      const { currentVersion: _dropped, ...rest } = outbox
      await this.store.write('sync_outbox', { ...rest, status: 'pending', baseVersion: base, updatedAt: Date.now() })
      if (tracked) await this.store.write('canvas_working_copies', { ...tracked, version: base })
      return this.#flushNow(id)
    })
  }

  // List-page mount hook: push any outbox whose asset uploads finished while
  // no canvas page was open — that push is what refreshes the canvas_index
  // cover/count projection.
  resumePending() {
    if (this.readOnly) return Promise.resolve({ ok: true, readOnly: true })
    if (this.quiesced) return Promise.reject(repositoryLifecycleError('canvas_repository_quiesced'))
    return this.#flushReadyOutboxes({ force: true })
  }

  subscribe(id, listener) {
    if (this.quiesced) return () => {}
    const sb = this.supabase()
    if (!sb) return () => {}
    const channel = sb.channel(`canvas:${id}:${crypto.randomUUID()}`).on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'synced_records', filter: `entity_id=eq.${id}` },
      payload => {
        if (this.quiesced) return
        const row = payload.new || payload.old
        if (!row || row.kind !== 'canvas') return
        if (row.deleted) {
          // Deletion echoes are never suppressible: an accepted local delete
          // and a remote delete both establish durable anti-resurrection
          // evidence before any stale editor/outbox can run again.
          void this.#chain(id, async () => {
            const marker = await this.#recordTombstone(id, row)
            this.#status(id, 'deleted', { version: marker.version, dirty: false })
            listener(row)
          }).catch(() => {})
          return
        }
        if (row.device_id === this.writerId) return
        listener(row)
      },
    ).subscribe()
    this.channels.set(id, channel)
    return () => { this.channels.delete(id); void sb.removeChannel(channel) }
  }

  #assertActive() {
    if (this.quiesced) throw repositoryLifecycleError('canvas_repository_quiesced')
  }

  #abortable(request) {
    if (typeof request?.abortSignal === 'function') {
      return request.abortSignal(this.lifecycleController.signal)
    }
    return request
  }

  #throwIfDeleted(id, working, outbox) {
    if (working?.deleted || outbox?.deleted) throw canvasDeletedError(id)
  }

  async #recordTombstone(id, source = {}) {
    this.#assertActive()
    const [existing, outbox] = await Promise.all([
      this.store.read('canvas_working_copies', id),
      this.store.read('sync_outbox', id),
    ])
    this.#assertActive()
    const version = Math.max(
      Number(source.version) || 0,
      Number(source.current_version) || 0,
      Number(existing?.version) || 0,
      Number(outbox?.baseVersion) || 0,
      Number(outbox?.currentVersion) || 0,
    )
    const now = Date.now()
    const marker = {
      id,
      deleted: true,
      version,
      remote: existing?.remote === true || source.remote !== false,
      deletedAt: existing?.deletedAt || source.updated_at || now,
      updatedAt: now,
    }
    // Evidence first, cleanup second. A crash at any boundary either retains
    // the old recovery rows or leaves the marker; it can never leave an empty
    // state that a direct URL mistakes for a brand-new draft.
    await this.store.write('canvas_working_copies', marker)
    this.#assertActive()
    await Promise.all([
      this.store.delete('sync_outbox', id),
      this.store.delete('canvas_view_state', id),
    ])
    this.#assertActive()
    clearTimeout(this.viewTimers.get(id))
    this.viewTimers.delete(id)
    this.#clearRetry(id)
    return marker
  }

  #scheduleViewPush(id) {
    if (this.quiesced) return
    const sb = this.supabase()
    if (!sb?.from || !sb?.rpc) return
    clearTimeout(this.viewTimers.get(id))
    this.viewTimers.set(id, setTimeout(() => {
      this.viewTimers.delete(id)
      if (this.quiesced) return
      void this.flushView(id).catch(() => {})
    }, this.viewSyncDelay))
  }

  #retryDelay(attempts) {
    const exponent = Math.max(0, Math.min(16, Number(attempts || 1) - 1))
    const capped = Math.min(this.retryMaxDelay, this.retryBaseDelay * (2 ** exponent))
    // Full fleets reconnecting together must not hammer sync_push in lockstep.
    // ±25% jitter remains bounded and is injectable for deterministic tests.
    const jitter = 0.75 + Math.max(0, Math.min(1, Number(this.random()) || 0)) * 0.5
    return Math.max(1, Math.round(capped * jitter))
  }

  #clearRetry(id) {
    clearTimeout(this.retryTimers.get(id))
    this.retryTimers.delete(id)
  }

  #scheduleRetry(id, delay) {
    if (this.quiesced) return
    this.#clearRetry(id)
    const timer = setTimeout(() => {
      this.retryTimers.delete(id)
      if (this.quiesced) return
      void this.#retryOutbox(id).catch(() => {})
    }, Math.max(0, Number(delay) || 0))
    timer.unref?.()
    this.retryTimers.set(id, timer)
  }

  async #retryOutbox(id) {
    this.#assertActive()
    const row = await this.store.read('sync_outbox', id)
    this.#assertActive()
    if (!row || row.status !== 'pending') return
    if (!row.deleted && !(await this.assetMap.allCommitted(row.document))) return
    this.#assertActive()
    await this.flush(id)
  }

  async #flushReadyOutboxes(options = {}) {
    this.#assertActive()
    const force = options.force === true
    const rows = await this.store.readAll('sync_outbox')
    this.#assertActive()
    for (const row of rows) {
      this.#assertActive()
      if (row.status !== 'pending') continue
      if (!row.deleted && !(await this.assetMap.allCommitted(row.document))) continue
      this.#assertActive()
      if (!force && Number(row.nextRetryAt) > Date.now()) {
        this.#scheduleRetry(row.canvasId, Number(row.nextRetryAt) - Date.now())
        continue
      }
      await this.flush(row.canvasId)
    }
  }

  async #flushPendingViews() {
    this.#assertActive()
    const rows = await this.store.readAll('canvas_view_state')
    this.#assertActive()
    for (const row of rows) {
      this.#assertActive()
      if (row?.syncStatus !== 'pending' || !row.canvasId) continue
      await this.flushView(row.canvasId).catch(() => {})
    }
  }

  dispose() {
    if (this.quiesced) return false
    this.quiesced = true
    try { this.lifecycleController.abort() } catch {}
    this.onlineTarget?.removeEventListener?.('online', this.onOnline)
    this.assetMap.removeEventListener?.('upload-status', this.onAssetUploadStatus)
    for (const timer of this.viewTimers.values()) clearTimeout(timer)
    for (const timer of this.retryTimers.values()) clearTimeout(timer)
    this.viewTimers.clear()
    this.retryTimers.clear()
    for (const channel of this.channels.values()) {
      try { void this.supabase()?.removeChannel?.(channel) } catch {}
    }
    this.channels.clear()
    return true
  }

  async #markUploadFailure(hash) {
    this.#assertActive()
    const rows = await this.store.readAll('sync_outbox')
    this.#assertActive()
    for (const row of rows) {
      this.#assertActive()
      if (!JSON.stringify(row.document).includes(`asset://${hash}.`)) continue
      this.#status(row.canvasId, 'upload-error', { dirty: true, hash })
    }
  }

  #status(id, status, extra = {}) {
    if (this.quiesced) return
    this.dispatchEvent(new CustomEvent('status', { detail: { id, status, ...extra } }))
  }
}
